import { debounce } from "lodash";
import {
  CircleArrowDown,
  CircleArrowUp,
  CircleDollarSign,
  Search,
} from "lucide-react";
import Image from "next/image";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Asset } from "@/app/dashboard/transactions/types";
import {
  cn,
  formatNumber,
  percentageFormat,
  textColorBasedOnNumber,
} from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { getAssets } from "@/services/controllers/assets";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";

interface Props {
  defaultCoin?: {
    name: string;
    price: string | number;
  };
}

export function ProfitCalculator({ defaultCoin }: Props) {
  const [buyPrice, setBuyPrice] = useState<string>("");
  const [sellPrice, setSellPrice] = useState<string>("");
  const [investment, setInvestment] = useState<string>("");
  const [profit, setProfit] = useState<number>(0);
  const [totalExitAmount, setTotalExitAmount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [suggestions, setSuggestions] = useState<Asset[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);
  const ref = useRef<any>(null);

  useEffect(() => {
    const controller = new AbortController();
    if (searchTerm.length >= 3) {
      setLoading(true);
      setSuggestions([]);
      getAssets({
        signal: controller.signal,
        params: {
          search: searchTerm,
          limit: 30,
          offset: 0,
        },
      })
        .then((response) => {
          setSuggestions(response.data.data);
        })
        .finally(() => {
          setLoading(false);
        });
    }

    return () => {
      controller.abort();
    };
  }, [searchTerm]);

  useEffect(() => {
    if (defaultCoin) {
      ref.current.value = defaultCoin.name;
      setSellPrice(defaultCoin.price as string);
      setBuyPrice(defaultCoin.price as string);
    }
  }, [defaultCoin]);

  useEffect(() => {
    const xProfit =
      ((parseFloat(investment) || 0) / (parseFloat(buyPrice) || 1)) *
      ((parseFloat(sellPrice) || 0) - (parseFloat(buyPrice) || 0));
    setProfit(xProfit);

    const xTotalExitAmount =
      ((parseFloat(investment) || 0) / (parseFloat(buyPrice) || 1)) *
      (parseFloat(sellPrice) || 0);
    setTotalExitAmount(xTotalExitAmount);
  }, [buyPrice, sellPrice, investment]);

  const totalProfitPercentage = useMemo(
    () =>
      (((totalExitAmount || 0) - (parseFloat(investment) || 0)) /
        (parseFloat(investment) || 1)) *
      100,
    [investment, totalExitAmount],
  );

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
  }, []);

  const debouncedSearch = useMemo(() => {
    return debounce(handleSearch, 250);
  }, [handleSearch]);

  return (
    <Card className="w-full">
      <CardHeader className="mb-4">
        <CardTitle>{m.early_short_lion_ask()}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="bg-gray-100 dark:bg-slate-800 w-full flex items-center justify-between h-[100px] rounded-sm px-4">
          <div>
            <Label htmlFor="entry">{m.silly_level_husky_quiz()}</Label>
            <p
              id="entry"
              className={cn(
                "mt-2",
                textColorBasedOnNumber(
                  investment === "" ? 0 : parseFloat(investment),
                ),
              )}
            >
              {formatNumber(parseFloat(investment), true)}
            </p>
          </div>
          <div>
            <Label htmlFor="exit">{m.round_flat_pelican_emerge()}</Label>
            <p
              id="exit"
              className={cn("mt-2", textColorBasedOnNumber(totalExitAmount))}
            >
              {formatNumber(totalExitAmount, true)}
            </p>
          </div>
          <div>
            <Label htmlFor="profit">{m.awake_key_meerkat_aspire()}</Label>
            <div id="profit">
              <Badge
                variant={
                  profit === 0
                    ? "default"
                    : profit > 0
                    ? "success"
                    : "destructive"
                }
              >
                {percentageFormat(totalProfitPercentage, true)}%
              </Badge>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 mt-4 gap-4">
          <div className="relative">
            <Input
              placeholder={m.search()}
              className="w-full rounded-sm bg-background pl-9 py-2 text-sm focus-visible:ring-0 bg-gray-100 dark:bg-slate-800"
              onChange={debouncedSearch}
              ref={ref}
              onFocus={() => {
                setTimeout(() => setFocused(true), 500);
              }}
              onBlur={() => {
                setTimeout(() => setFocused(false), 100);
              }}
            />
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
            {focused && searchTerm.trim().length > 1 && (
              <div className="absolute left-0 z-10 mt-1 w-full rounded-lg border bg-background shadow-lg overflow-auto max-h-[150px]">
                {loading
                  ? [...Array(4)].map(() => (
                      <div
                        className="flex gap-4 w-full items-center h-[50px] px-4"
                        key={Math.random() * 10}
                      >
                        <Skeleton className="min-w-6 h-6" />
                        <Skeleton className="w-full h-4" />
                      </div>
                    ))
                  : suggestions.map((coin) => {
                      return (
                        <div className="h-[44px] overflow-auto" key={coin.id}>
                          <Button
                            className="flex items-center w-full justify-between rounded-sm"
                            variant="ghost"
                            onClick={() => {
                              ref.current.value = coin?.asset_data?.name;
                              if (coin?.asset_data?.current_price) {
                                setSellPrice(
                                  coin.asset_data.current_price.toString(),
                                );
                                setBuyPrice(
                                  coin.asset_data.current_price.toString(),
                                );
                              }
                            }}
                          >
                            <div className="flex items-center gap-3 max-w-[70%]">
                              <Image
                                src={
                                  coin?.asset_data?.image ?? "/placeholder.svg"
                                }
                                alt={coin?.asset_name ?? ""}
                                width={24}
                                height={24}
                                className="w-6 h-6"
                              />
                              <p className="font-medium text-wrap text-start">
                                {coin?.asset_data?.name}
                              </p>
                            </div>
                            <p className="font-semibold ">
                              {formatNumber(
                                coin?.asset_data?.current_price ?? 0,
                                true,
                              )}
                            </p>
                          </Button>
                        </div>
                      );
                    })}
              </div>
            )}
          </div>

          <div className="relative">
            <Input
              placeholder="Buy price"
              className="w-full rounded-sm bg-background pl-9 py-2 text-sm focus-visible:ring-0 bg-gray-100 dark:bg-slate-800"
              value={buyPrice}
              onChange={(e) => setBuyPrice(e.target.value)}
            />
            <CircleArrowDown className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
          </div>

          <div className="relative">
            <Input
              placeholder="Investment"
              className="w-full rounded-sm bg-background pl-9 py-2 text-sm focus-visible:ring-0 bg-gray-100 dark:bg-slate-800"
              value={investment}
              onChange={(e) => setInvestment(e.target.value)}
            />
            <CircleDollarSign className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
          </div>

          <div className="relative">
            <Input
              placeholder="Sell price"
              className="w-full rounded-sm bg-background pl-9 py-2 text-sm focus-visible:ring-0 bg-gray-100 dark:bg-slate-800"
              value={sellPrice}
              onChange={(e) => setSellPrice(e.target.value)}
            />
            <CircleArrowUp className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
